import { inject, Injectable } from '@angular/core';
import { Language } from '@lx/language';
import { DeepLinkParametersEnum, HostDataReaderService, JuniorCampModeStrategyHelperService, QuoteToolStrategyService } from '@lx/quote-tool/shared/services';
import { CourseClassificationEnum, ProductPackage } from '@lx/quote-tool/shared/types';
import { CourseDates } from '@lx/shared/data-access/course';
import { SchoolV4 } from '@lx/shared/data-access/school';
import { Country, Course, CourseType, SchoolTypesEnum } from '@lx/shared/data-access/types';
import { LoggerService } from '@lx/shared/logger';
import { SessionStorageService } from '@lx/shared/storage';
import { getDateFromString, getDateStringFromDate, isEmptyObj, normalizeString, QuoteToolPreselectedModes } from '@lx/shared/utils';
import { combineLatest, Observable, of, tap } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { z, ZodSafeParseResult } from 'zod';
import { JuniorCampDeepLinkParameterSchema } from '../../../schemas';
import { QuoteToolLiteState, SharedDataStore } from '../../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { QTLiteJuniorCampDeepLinkParameters, RouteIds } from '../../../types';
import { CourseService } from '../../course.service';
import { StepDefinitionService } from '../../step-definition.service';
import { Flows, getQuoteToolStorageKey, SchoolLocation } from '@lx/quote-tool/lite/shared/types';

@Injectable({
  providedIn: 'root',
})
export class JuniorCampModeStrategyService implements QuoteToolStrategyService<RouteIds> {
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly courseService = inject(CourseService);
  private readonly juniorCampModeStrategyHelper = inject(JuniorCampModeStrategyHelperService);
  private readonly logger = inject(LoggerService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly stepDefinitionService = inject(StepDefinitionService);
  private readonly hostDataReader = inject(HostDataReaderService);
  private readonly storageKey = getQuoteToolStorageKey();

  getQuoteToolMode(): QuoteToolPreselectedModes {
    return QuoteToolPreselectedModes.juniorCamp;
  }

  getStepToNavigate(): Observable<RouteIds> | RouteIds {
    return of(RouteIds.course);
  }

  loadData$(): Observable<boolean> {
    const deepLinkParameters = this.getDeepLinkParameters();
    const parsedParameters = this.parseDeepLinkParameters(deepLinkParameters);

    if (!parsedParameters.success) {
      this.logger.error('Invalid deep link parameters', z.prettifyError(parsedParameters.error));
      return of(false);
    }

    const { schoolCode, packageCode, duration, startDate, endDate, languageCode, deeplinkType } = deepLinkParameters;

    return combineLatest([this.sharedDataStore.schools$, this.sharedDataStore.countries$, this.sharedDataStore.languages$.pipe(take(1))]).pipe(
      switchMap(([schools, countries, languages]: [SchoolV4[], Country[], Language[]]) => {
        const selectedSchool = schools.find((s) => normalizeString(s.code) === normalizeString(schoolCode));
        const selectedCountry = selectedSchool ? countries.find((c) => normalizeString(c.code) === normalizeString(selectedSchool.country?.code)) : undefined;
        const selectedLanguage = this.getSelectedLanguage(schoolCode, languageCode, schools, languages);

        // If no school was provided or found, skip course fetching
        const courses$ = selectedSchool ? this.courseService.getJuniorCourses(selectedSchool.code) : of([]);

        return combineLatest([of(selectedSchool), of(selectedCountry), courses$, of(selectedLanguage)]);
      }),
      map(([selectedSchool, selectedCountry, courses, selectedLanguage]: [SchoolV4, Country, Course[], Language]) => {
        // Try to find the matching course if a package code was provided
        const course = courses?.find((c) => normalizeString(c.code) === normalizeString(packageCode));

        // The user needs to provide packageCode and schoolCode when the duration and/or startDate and endDate are provided
        if ((duration || (startDate && endDate)) && !(packageCode && schoolCode)) {
          this.logger.error('Package or School not found', `${packageCode} - ${schoolCode}`);
          return undefined;
        }

        // Log error only if a package code was provided but no course was found
        if (packageCode && !course) {
          this.logger.error('Course not found', packageCode);
          return undefined;
        }

        // If the language is provided from queryParams but the language was not found then we should show to the user the default state
        if (languageCode && !selectedLanguage) {
          this.logger.error('Language not found', languageCode);
          return undefined;
        }

        // Always execute prepareJuniorCampForStore, even with partial data
        return this.prepareJuniorCampForStore(selectedSchool, course, selectedCountry, duration, startDate, endDate, selectedLanguage?.code, deeplinkType);
      }),
      tap((juniorCamp: Partial<QuoteToolLiteState>) => {
        if (!isEmptyObj(juniorCamp)) {
          this.sessionStorageService.set(this.storageKey, { ...juniorCamp });
        }
      }),
      map((selectedData: Partial<QuoteToolLiteState>) => !isEmptyObj(selectedData)),
      catchError((error) => {
        this.logger.error('Error during loadData$', error);
        return of(false);
      }),
    );
  }

  private getSelectedLanguage(schoolCode: string, languageCode: string, schools: SchoolV4[], languages: Language[]): Language {
    if (schoolCode) {
      const school = schools.find((s) => normalizeString(s.code) === normalizeString(schoolCode));
      const courseLanguages = school?.courseLanguages[SchoolTypesEnum.JUNIOR] || [];

      const validLanguageCode = courseLanguages.includes(languageCode ?? '') ? languageCode : courseLanguages[0];

      return languages.find((l) => normalizeString(l.code) === normalizeString(validLanguageCode));
    }

    if (languageCode) {
      return languages.find((l) => normalizeString(l.code) === normalizeString(languageCode));
    }

    return undefined;
  }

  private getDeepLinkParameters(): QTLiteJuniorCampDeepLinkParameters {
    // Get query params from URL
    const queryParams = this.hostDataReader.getQueryParams();
    const deepLinkParameters: QTLiteJuniorCampDeepLinkParameters = this.juniorCampModeStrategyHelper.getDeepLinkParameters();
    return { ...deepLinkParameters, languageCode: queryParams.get(DeepLinkParametersEnum.languageCode) || undefined };
  }

  private parseDeepLinkParameters(deepLinkParameters: QTLiteJuniorCampDeepLinkParameters): ZodSafeParseResult<Partial<QTLiteJuniorCampDeepLinkParameters>> {
    return JuniorCampDeepLinkParameterSchema.safeParse(deepLinkParameters);
  }

  private prepareJuniorCampForStore(
    school: SchoolV4,
    course: Course,
    country: Country,
    duration: number,
    startDate: Date,
    endDate: Date,
    languageCode: string,
    deeplinkType: string,
  ): Partial<QuoteToolLiteState & { currentStepId: string }> {
    const { code: schoolCode, name: schoolName, ageMin, ageMax } = school || {};
    const { code: countryCode, name: countryName } = country || {};
    const { type, code, productPackage, courseClassification, durationMin, durationMax, courseDates, courseLanguage } = course || {};

    const { validatedStartDate, validatedEndDate } = this.getValidatedStartAndEndDates(courseDates, startDate, endDate);

    return {
      currentStepId: this.stepDefinitionService.getStepDefinition(RouteIds.course, Flows.Junior)?.id,
      activeFlow: Flows.Junior,
      selectedLanguage: courseLanguage || languageCode,
      selectedSchoolLocation: this.getSelectedSchoolLocation(school, schoolCode, schoolName, ageMin, ageMax, countryCode, countryName),
      selectedCourse: this.getSelectedCourse(course, type, code, courseClassification, durationMin, durationMax, courseDates, productPackage),
      selectedDuration: duration,
      selectedStartDate: getDateStringFromDate(validatedStartDate),
      selectedEndDate: getDateStringFromDate(validatedEndDate),
      deeplinkType,
    };
  }

  private getSelectedCourse(
    course: Course,
    type: CourseType,
    code: string,
    courseClassification: string,
    durationMin: number,
    durationMax: number,
    courseDates: CourseDates[],
    productPackage: ProductPackage,
  ): CourseDetails {
    return course
      ? {
          type: type?.code,
          code,
          name: courseClassification?.toLocaleLowerCase() === CourseClassificationEnum.standard ? 'Standard' : 'Intensive',
          durationMin,
          durationMax,
          courseDates,
          productPackage,
        }
      : undefined;
  }

  private getSelectedSchoolLocation(
    school: SchoolV4,
    schoolCode: string,
    schoolName: string,
    ageMin: number,
    ageMax: number,
    countryCode: string,
    countryName: string,
  ): SchoolLocation {
    return school
      ? {
          schoolCode,
          schoolName: `${schoolName} (${ageMin}-${ageMax})`,
          countryCode,
          countryName,
        }
      : undefined;
  }

  /**
   * Validates the start and end dates for junior camp courses.
   * If the provided start date doesn't match any course date or is undefined, falls back to the first available date.
   * Similarly, validates the end date based on the selected start date.
   */
  private getValidatedStartAndEndDates(
    courseDates: CourseDates[] | undefined,
    startDate: Date | undefined,
    endDate: Date | undefined,
  ): { validatedStartDate: Date | undefined; validatedEndDate: Date | undefined } {
    if (!courseDates?.length) {
      this.logger.warn('No courseDates available for the selected course');
      return { validatedStartDate: undefined, validatedEndDate: undefined };
    }

    const startDateString = getDateStringFromDate(startDate);
    const endDateString = getDateStringFromDate(endDate);

    // Find a matching course date where both start and end dates match the provided dates
    const matchingCourseDate = courseDates.find(({ from, until }) => from === startDateString && until === endDateString);

    if (matchingCourseDate) {
      return { validatedStartDate: startDate, validatedEndDate: endDate };
    }

    // If no exact match, try to find a course date that matches only the start date
    const matchingStartDate = courseDates.find((courseDate) => courseDate.from === startDateString);

    if (matchingStartDate) {
      this.logger.warn('Start date found but end date does not match, using the first available end date for this start date');
      return {
        validatedStartDate: startDate,
        validatedEndDate: getDateFromString(matchingStartDate.until),
      };
    }

    // If neither start nor end date match, use the first available course date
    this.logger.warn('Invalid or missing start date and end date for the selected course, taking the first available ones');
    const firstAvailableDate = courseDates[0];

    return {
      validatedStartDate: getDateFromString(firstAvailableDate.from),
      validatedEndDate: getDateFromString(firstAvailableDate.until),
    };
  }
}
